import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
      flexRender,
      getCoreRowModel,
      type SortingState,
      useReactTable,
      type VisibilityState,
} from "@tanstack/react-table";
import { Pencil, Plus, Sparkles } from "lucide-react";
import * as React from "react";
import { useEffect } from "react";
import { toast } from "sonner";
import { TableCardSkeleton } from "@/components/loading";
import {
      DashboardContentZone,
      DashboardControlRow,
} from "@/components/patterns/content-zone";
import {
      DataTableBandHeader,
      DataTableFooter,
      DataTableHeaderRow,
      DataTableSurface,
      dataTableBodyCellClassName,
      dataTableBodyRowClassName,
} from "@/components/patterns/table-chrome";
import { Button } from "@/components/ui/button";
import {
      DropdownMenu,
      DropdownMenuContent,
      DropdownMenuItem,
      DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@/components/ui/table";
import { useDebounce } from "@/hooks/use-debounce";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePaginate } from "@/hooks/use-paginate";
import { useTRPC } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import type {
      PaginatedBrandPrompts,
      SearchSpecificationPromptListItem,
      SearchSpecificationPromptMetric,
} from "@/types/data";
import { CreatePromptsDialog } from "../create-prompts-dialog/create-prompts-dialog";
import { GeneratePromptsDialog } from "../create-prompts-dialog/generate-prompts-dialog";
import { createColumns, type PromptTableRow } from "./columns";

type SortField =
      | "text"
      | "promptType"
      | "status"
      | "createdAt"
      | "responseCount"
      | "visibilityScore"
      | "avgSentiment"
      | "mentionRate"
      | "averageRanking";
type SortOrder = "asc" | "desc";

interface PromptsTableProps {
      callbackFn?: (params: {
            skip: number;
            take: number;
            sortBy?: SortField;
            sortOrder?: SortOrder;
            textFilter?: string;
      }) => Promise<PaginatedBrandPrompts>;
      searchSpecificationId?: string;
}

const metricSortFields = new Set<SortField>([
      "responseCount",
      "visibilityScore",
      "avgSentiment",
      "mentionRate",
      "averageRanking",
]);

const toPromptTableRow = (prompt: {
      id: string;
      text: string;
      promptType: "ORGANIC" | "COMPETITOR" | "BRAND_SPECIFIC";
      searchSpecificationId: string;
      status: "ACTIVE" | "INACTIVE";
      createdAt: string;
      updatedAt?: string | null;
      responseCount?: number;
      visibilityScore?: number;
      avgSentiment?: number | null;
      mentionRate?: number;
      averageRanking?: number | null;
}): PromptTableRow => ({
      ...prompt,
      responseCount: prompt.responseCount ?? 0,
      visibilityScore: prompt.visibilityScore ?? 0,
      avgSentiment: prompt.avgSentiment ?? null,
      mentionRate: prompt.mentionRate ?? 0,
      averageRanking: prompt.averageRanking ?? null,
      isMetricsLoading: false,
});

const toSearchPromptTableRow = (
      prompt: SearchSpecificationPromptListItem,
      metric: SearchSpecificationPromptMetric | undefined,
      isMetricsLoading: boolean,
): PromptTableRow => ({
      ...prompt,
      responseCount: metric?.responseCount ?? 0,
      visibilityScore: metric?.visibilityScore ?? 0,
      avgSentiment: metric?.avgSentiment ?? null,
      mentionRate: metric?.mentionRate ?? 0,
      averageRanking: metric?.averageRanking ?? null,
      isMetricsLoading: !metric && isMetricsLoading,
});

export function PromptsTable({ callbackFn, searchSpecificationId }: PromptsTableProps) {
      const trpc = useTRPC();
      const noBatchRequest = { trpc: { context: { noBatch: true } } } as const;
      const isMobile = useIsMobile();

      const { skip, take, pagination, setPagination } = usePaginate();

      const [sorting, setSorting] = React.useState<SortingState>([]);
      const [textFilter, setTextFilter] = React.useState("");
      const debouncedTextFilter = useDebounce(textFilter, 300);

      // Extract sort params from sorting state
      const sortBy = sorting[0]?.id as SortField | undefined;
      const sortOrder = sorting[0]?.desc ? "desc" : "asc";
      const isSearchSpecificationMode = Boolean(searchSpecificationId);
      const isMetricSort = sortBy ? metricSortFields.has(sortBy) : false;

      const {
            data: callbackPrompts,
            isLoading: isLoadingCallbackPrompts,
      } = useQuery({
            queryKey: ["prompts", "callback", skip, take, sortBy, sortOrder, debouncedTextFilter],
            queryFn: () =>
                  callbackFn!({
                        skip,
                        take,
                        sortBy,
                        sortOrder: sortBy ? sortOrder : undefined,
                        textFilter: debouncedTextFilter || undefined,
                  }),
            enabled: !isSearchSpecificationMode && !!callbackFn,
            placeholderData: keepPreviousData,
      });

      const searchPromptListQuery = useQuery(
            trpc.prompts.getSearchSpecificationPromptList.queryOptions(
                  {
                        searchSpecificationId: searchSpecificationId ?? "",
                        skip,
                        take,
                        sortBy: sortBy as "text" | "promptType" | "status" | "createdAt" | undefined,
                        sortOrder: sortBy ? sortOrder : undefined,
                        textFilter: debouncedTextFilter || undefined,
                  },
                  {
                        enabled: isSearchSpecificationMode && !isMetricSort,
                        placeholderData: keepPreviousData,
                        ...noBatchRequest,
                  },
            ),
      );

      const promptIds = React.useMemo(
            () => searchPromptListQuery.data?.items.map((prompt) => prompt.id) ?? [],
            [searchPromptListQuery.data],
      );

      const searchPromptMetricsQuery = useQuery(
            trpc.prompts.getSearchSpecificationPromptMetrics.queryOptions(
                  {
                        searchSpecificationId: searchSpecificationId ?? "",
                        promptIds,
                  },
                  {
                        enabled: isSearchSpecificationMode && !isMetricSort && promptIds.length > 0,
                        ...noBatchRequest,
                  },
            ),
      );

      const searchPromptMetricSortQuery = useQuery(
            trpc.prompts.getSearchSpecificationPrompts.queryOptions(
                  {
                        searchSpecificationId: searchSpecificationId ?? "",
                        skip,
                        take,
                        sortBy,
                        sortOrder: sortBy ? sortOrder : undefined,
                        textFilter: debouncedTextFilter || undefined,
                  },
                  {
                        enabled: isSearchSpecificationMode && isMetricSort,
                        placeholderData: keepPreviousData,
                        ...noBatchRequest,
                  },
            ),
      );
      const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
      const [rowSelection, setRowSelection] = React.useState({});
      const queryClient = useQueryClient();
      const [isCreatePromptsDialogOpen, setIsCreatePromptsDialogOpen] = React.useState(false);
      const [isGeneratePromptsDialogOpen, setIsGeneratePromptsDialogOpen] = React.useState(false);
      const navigate = useNavigate();

      const params = useParams({ strict: false });

      useEffect(() => {
            setColumnVisibility(
                  isMobile
                        ? {
                                visibilityScore: false,
                                avgSentiment: false,
                                mentionRate: false,
                                averageRanking: false,
                          }
                        : {},
            );
      }, [isMobile]);

      // Handle row click to navigate to responses
      const handleRowClick = (prompt: PromptTableRow) => {
            if (params.brand && prompt.id && prompt.searchSpecificationId) {
                  navigate({
                        to: "/$brand/searches/$search/responses",
                        params: {
                              brand: params.brand,
                              search: prompt.searchSpecificationId,
                        },
                        search: {
                              promptId: prompt.id || undefined,
                        },
                  });
            }
      };

      const prompts = React.useMemo(() => {
            if (isSearchSpecificationMode) {
                  if (isMetricSort) {
                        if (!searchPromptMetricSortQuery.data) {
                              return undefined;
                        }

                        return {
                              ...searchPromptMetricSortQuery.data,
                              items: searchPromptMetricSortQuery.data.items.map((prompt) => toPromptTableRow(prompt)),
                        };
                  }

                  if (!searchPromptListQuery.data) {
                        return undefined;
                  }

                  const metricsByPromptId = new Map(
                        (searchPromptMetricsQuery.data ?? []).map((metric) => [metric.promptId, metric]),
                  );
                  const isMetricsLoading =
                        promptIds.length > 0 &&
                        (searchPromptMetricsQuery.isLoading || searchPromptMetricsQuery.isFetching);

                  return {
                        ...searchPromptListQuery.data,
                        items: searchPromptListQuery.data.items.map((prompt) =>
                              toSearchPromptTableRow(
                                    prompt,
                                    metricsByPromptId.get(prompt.id),
                                    isMetricsLoading,
                              ),
                        ),
                  };
            }

            if (!callbackPrompts) {
                  return undefined;
            }

            return {
                  ...callbackPrompts,
                  items: callbackPrompts.items.map((prompt) => toPromptTableRow(prompt)),
            };
      }, [
            callbackPrompts,
            isMetricSort,
            isSearchSpecificationMode,
            promptIds,
            searchPromptListQuery.data,
            searchPromptMetricSortQuery.data,
            searchPromptMetricsQuery.data,
            searchPromptMetricsQuery.isFetching,
            searchPromptMetricsQuery.isLoading,
      ]);

      const invalidatePromptQueries = () => {
            queryClient.invalidateQueries({ queryKey: ["prompts"] });

            if (!searchSpecificationId) {
                  return;
            }

            queryClient.invalidateQueries({ queryKey: trpc.prompts.getSearchSpecificationPromptList.queryKey() });
            queryClient.invalidateQueries({ queryKey: trpc.prompts.getSearchSpecificationPromptMetrics.queryKey() });
            queryClient.invalidateQueries({ queryKey: trpc.prompts.getSearchSpecificationPrompts.queryKey() });
            queryClient.invalidateQueries({
                  queryKey: trpc.searchSpecifications.getSearchSpecificationPromptsCount.queryKey({
                        id: searchSpecificationId,
                  }),
            });
            queryClient.invalidateQueries({ queryKey: trpc.searchSpecifications.getSearchSpecificationMetrics.queryKey() });
      };

      const updatePromptStatusMutation = useMutation(
            trpc.prompts.updatePromptStatus.mutationOptions({
                  onSuccess: () => {
                        invalidatePromptQueries();
                  },
            }),
      );

      const deletePromptMutation = useMutation(
            trpc.prompts.deletePrompt.mutationOptions({
                  onSuccess: () => {
                        invalidatePromptQueries();
                        toast.success("Prompt archived. You can restore it from Activity History.");
                  },
                  onError: (error) => {
                        toast.error(error.message || "Failed to archive prompt");
                  },
            }),
      );

      const columns = createColumns(deletePromptMutation, updatePromptStatusMutation, handleRowClick);

      // Reset to first page when filter changes
      useEffect(() => {
            void debouncedTextFilter;
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      }, [debouncedTextFilter, setPagination]);

      const table = useReactTable({
            data: prompts?.items ?? [],
            columns,
            onSortingChange: (updater) => {
                  // Reset to first page when sorting changes
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                  setSorting(updater);
            },
            getCoreRowModel: getCoreRowModel(),
            manualPagination: true,
            manualSorting: true, // Server-side sorting
            manualFiltering: true, // Server-side filtering
            rowCount: prompts?.totalCount ?? 0,
            onPaginationChange: setPagination,
            onColumnVisibilityChange: setColumnVisibility,
            onRowSelectionChange: setRowSelection,
            state: {
                  sorting,
                  columnVisibility,
                  rowSelection,
                  pagination,
            },
      });

      const isLoading =
            (isSearchSpecificationMode &&
                  ((isMetricSort && searchPromptMetricSortQuery.isLoading && !searchPromptMetricSortQuery.data) ||
                        (!isMetricSort && searchPromptListQuery.isLoading && !searchPromptListQuery.data))) ||
            (!isSearchSpecificationMode && isLoadingCallbackPrompts && !callbackPrompts);

      if (isLoading) {
            return <TableCardSkeleton rows={6} columns={6} showToolbar showFooter />;
      }

      return (
            <DashboardContentZone>
                  <DashboardControlRow>
                        <Input
                              placeholder="Filter prompts..."
                              value={textFilter}
                              onChange={(event) => setTextFilter(event.target.value)}
                              variant="surface"
                              className="w-full sm:max-w-sm"
                        />
                        {searchSpecificationId && (
                              <DropdownMenu>
                                    <DropdownMenuTrigger className="cursor-pointer">
                                          <Button variant="outline" className="bg-card">
                                                <Plus className="mr-2 h-4 w-4" />
                                                Add Prompts
                                          </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                          <DropdownMenuItem
                                                className="cursor-pointer"
                                                onClick={() => setIsGeneratePromptsDialogOpen(true)}
                                          >
                                                <Sparkles className="mr-2 h-4 w-4" />
                                                Generate
                                          </DropdownMenuItem>
                                          <DropdownMenuItem
                                                className="cursor-pointer"
                                                onClick={() => setIsCreatePromptsDialogOpen(true)}
                                          >
                                                <Pencil className="mr-2 h-4 w-4" />
                                                Enter Manually
                                          </DropdownMenuItem>
                                    </DropdownMenuContent>
                              </DropdownMenu>
                        )}
                  </DashboardControlRow>
                  <DataTableSurface>
                        <Table>
                              <DataTableBandHeader>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                          <DataTableHeaderRow key={headerGroup.id}>
                                                {headerGroup.headers.map((header) => {
                                                      return (
                                                            <TableHead key={header.id}>
                                                                  {header.isPlaceholder
                                                                        ? null
                                                                        : flexRender(
                                                                                header.column.columnDef.header,
                                                                                header.getContext(),
                                                                          )}
                                                            </TableHead>
                                                                );
                                                      })}
                                          </DataTableHeaderRow>
                                    ))}
                              </DataTableBandHeader>
                              <TableBody>
                                    {table.getRowModel().rows?.length ? (
                                          table.getRowModel().rows.map((row) => (
                                                <TableRow
                                                      key={row.id}
                                                      data-state={row.getIsSelected() && "selected"}
                                                      className={cn(
                                                            dataTableBodyRowClassName,
                                                            row.original.status === "INACTIVE" && "opacity-50",
                                                      )}
                                                >
                                                      {row.getVisibleCells().map((cell) => (
                                                            <TableCell
                                                                  key={cell.id}
                                                                  className={cn(dataTableBodyCellClassName, "py-1")}
                                                            >
                                                                  {flexRender(
                                                                        cell.column.columnDef.cell,
                                                                        cell.getContext(),
                                                                  )}
                                                            </TableCell>
                                                      ))}
                                                </TableRow>
                                          ))
                                    ) : (
                                          <TableRow>
                                                <TableCell colSpan={table.getVisibleFlatColumns().length} className="h-24 text-center text-brand-ink/50">
                                                      No prompts found.
                                                </TableCell>
                                          </TableRow>
                                    )}
                              </TableBody>
                        </Table>
                  </DataTableSurface>

                  <DataTableFooter
                        summary={`${prompts?.totalCount ?? 0} prompt(s) total.`}
                        onPrevious={() => table.previousPage()}
                        onNext={() => table.nextPage()}
                        canPrevious={table.getCanPreviousPage()}
                        canNext={table.getCanNextPage()}
                  />

                  {searchSpecificationId && (
                        <>
                              <CreatePromptsDialog
                                    isOpen={isCreatePromptsDialogOpen}
                                    setIsOpen={setIsCreatePromptsDialogOpen}
                                    searchSpecificationId={searchSpecificationId}
                              />
                              <GeneratePromptsDialog
                                    isOpen={isGeneratePromptsDialogOpen}
                                    setIsOpen={setIsGeneratePromptsDialogOpen}
                                    searchSpecificationId={searchSpecificationId}
                              />
                        </>
                  )}
            </DashboardContentZone>
      );
}
